import { Checkbox, Select, SelectOptionType } from '@bp/ui-components';
import classNames from 'classnames';
import { Field, FieldArrayRenderProps, useFormikContext } from 'formik';
import { ChangeEvent } from 'react';
import { SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import { formatNumber } from '../../../utils/helper';
import { InheritDeputateFormType } from './InheritDeputateForm';
import styles from './InheritDeputateFormRow.module.scss';

type InheritDeputateFormRowProps = {
  index: number;
  originalTeacher: boolean;
  originalSubject: boolean;
  originalClass: boolean;
  helper: FieldArrayRenderProps;
};

export const InheritDeputateFormRow = ({
  index,
  originalTeacher,
  originalSubject,
  originalClass,
  helper,
}: InheritDeputateFormRowProps) => {
  const { values, setFieldValue, errors } = useFormikContext<InheritDeputateFormType>();

  const {
    selected,
    teacherName,
    hadLastYearTeacher,
    subjectName,
    hadLastYearSubject,
    className,
    hadLastYearClass,
    hours,
    comment,
  } = values[index];

  const { teacherData, subjectData, classesData } = useLoadBasicData({ pause: false });

  const teacherOptions = useCreateSelectOptions(
    teacherData?.people.filter((t) => t.active),
    'uuid',
    'listName',
  );

  const subjectOptions = useCreateSelectOptions(
    subjectData?.subjects.filter((s) => s.active),
    'uuid',
    'name',
  );

  const classOptions = useCreateSelectOptions(classesData?.classes, 'uuid', 'name');

  return (
    <div className={classNames(styles['inherit-deputate-form-row'], { [styles.selected]: selected })}>
      <div className={styles.checkbox}>
        <Field
          as={Checkbox}
          name={`${helper.name}.${index}.selected`}
          checked={selected}
          onChange={async (e: ChangeEvent<HTMLInputElement>) =>
            await setFieldValue(`${index}.selected`, e.target.checked)
          }
        />
      </div>

      <div className={styles.large}>
        {originalTeacher ? (
          teacherName
        ) : hadLastYearTeacher && !originalTeacher ? (
          <Field
            as={Select}
            className={styles.select}
            name={`${helper.name}.${index}.teacher`}
            options={teacherOptions}
            onChange={async (option: SingleValue<SelectOptionType>) => {
              await setFieldValue(`${index}.teacherUuid`, option?.value);
              await setFieldValue(`${index}.teacherName`, option?.label);
            }}
            menuPosition='fixed'
            dense
            isSearchable
            disabled={!selected}
            error={errors[index]?.teacherUuid}
          />
        ) : (
          ''
        )}
      </div>

      <div className={styles.large}>
        {originalSubject ? (
          subjectName
        ) : hadLastYearSubject && !originalSubject ? (
          <Field
            as={Select}
            className={styles.select}
            name={`${helper.name}.${index}.subject`}
            options={subjectOptions}
            onChange={async (option: SingleValue<SelectOptionType>) => {
              await setFieldValue(`${index}.subjectUuid`, option?.value);
              await setFieldValue(`${index}.subjectName`, option?.label);
            }}
            menuPosition='fixed'
            dense
            isSearchable
            isClearable
            disabled={!selected}
          />
        ) : (
          ''
        )}
      </div>

      <div className={styles.large}>
        {originalClass ? (
          className
        ) : hadLastYearClass && !originalClass ? (
          <Field
            as={Select}
            className={styles.select}
            name={`${helper.name}.${index}.class`}
            options={classOptions}
            onChange={async (option: SingleValue<SelectOptionType>) => {
              await setFieldValue(`${index}.classUuid`, option?.value);
              await setFieldValue(`${index}.className`, option?.label);
            }}
            menuPosition='fixed'
            dense
            isSearchable
            isClearable
            disabled={!selected}
          />
        ) : (
          ''
        )}
      </div>

      <div className={styles.small}>{formatNumber(parseFloat(hours))}</div>
      <div className={styles.medium}>{comment}</div>
    </div>
  );
};
